import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { TooltipContentProps } from 'recharts';

import { formatCompactMoney } from '../../../utils/formatMoney';
import type { DashboardChartPoint } from '../types';
import { nextStableChartBoxSize, type ChartBoxSize } from '../utils/chartBoxSize';
import { CHART_COLORS, formatChartCount } from '../utils/chartConfig';
import { DashboardChartTooltip } from './DashboardChartTooltip';

type ChartMode = 'umsatz' | 'angebote' | 'anfragen';

const MODES: Array<{ id: ChartMode; label: string; title: string }> = [
  { id: 'umsatz', label: 'Umsatz', title: 'Umsatz' },
  { id: 'angebote', label: 'Angebote', title: 'Angebote' },
  { id: 'anfragen', label: 'Anfragen', title: 'Anfragen' },
];

type SeriesDef = { dataKey: string; name: string; color: string };

const SERIES_BY_MODE: Record<ChartMode, SeriesDef[]> = {
  umsatz: [
    { dataKey: 'umsatzPaid', name: 'Bezahlt', color: CHART_COLORS.umsatzPaid },
    { dataKey: 'umsatzOpen', name: 'Offen', color: CHART_COLORS.umsatzOpen },
  ],
  angebote: [
    { dataKey: 'angeboteGewonnen', name: 'Gewonnen', color: CHART_COLORS.gewonnen },
    { dataKey: 'angeboteAusstehend', name: 'Ausstehend', color: CHART_COLORS.ausstehend },
  ],
  anfragen: [{ dataKey: 'anfragen', name: 'Anfragen', color: CHART_COLORS.anfragen }],
};

const CHART_MARGIN = { top: 8, right: 8, left: 0, bottom: 0 };
const CHART_CURSOR = { fill: 'rgba(148,163,184,0.12)' };
const X_AXIS_TICK = { fontSize: 11, fill: 'var(--color-app-text-subtle)' };
const Y_AXIS_TICK = { fontSize: 10, fill: 'var(--color-app-text-subtle)' };

type DashboardAngeboteChartProps = {
  points: DashboardChartPoint[];
  subtitle: string;
  availableModes: {
    umsatz: boolean;
    angebote: boolean;
    anfragen: boolean;
  };
};

/** Measure the chart host with clientWidth only — mixing contentRect caused 1px loops with Recharts axes. */
function useRoundedBoxSize(): [React.RefObject<HTMLDivElement | null>, ChartBoxSize] {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<ChartBoxSize>({ width: 0, height: 0 });
  const sizeRef = useRef(size);
  sizeRef.current = size;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    let frame = 0;
    const applyFromElement = () => {
      const next = nextStableChartBoxSize(sizeRef.current, el.clientWidth, el.clientHeight);
      if (!next) return;
      sizeRef.current = next;
      setSize(next);
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(applyFromElement);
    };

    applyFromElement();
    const observer = new ResizeObserver(schedule);
    observer.observe(el);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  return [ref, size];
}

function MoneyTooltip(props: TooltipContentProps) {
  return <DashboardChartTooltip active={props.active} label={props.label} payload={props.payload} valueFormat="money" />;
}

function CountTooltip(props: TooltipContentProps) {
  return <DashboardChartTooltip active={props.active} label={props.label} payload={props.payload} valueFormat="count" />;
}

export function DashboardAngeboteChart({ points, subtitle, availableModes }: DashboardAngeboteChartProps) {
  const allowedModes = useMemo(
    () => MODES.filter((m) => availableModes[m.id]),
    [availableModes.umsatz, availableModes.angebote, availableModes.anfragen],
  );
  const [mode, setMode] = useState<ChartMode>(() => allowedModes[0]?.id ?? 'umsatz');
  const [chartBoxRef, chartSize] = useRoundedBoxSize();
  const series = SERIES_BY_MODE[mode];
  const valueFormat = mode === 'anfragen' ? 'count' : 'money';
  const activeMode = allowedModes.find((m) => m.id === mode) ?? allowedModes[0];

  useEffect(() => {
    if (!allowedModes.length) return;
    if (allowedModes.some((m) => m.id === mode)) return;
    setMode(allowedModes[0]!.id);
  }, [allowedModes, mode]);

  const hasData = useMemo(
    () => points.some((p) => series.some((s) => Number((p as unknown as Record<string, number>)[s.dataKey]) > 0)),
    [points, series],
  );

  const formatYTick = useCallback(
    (v: number) => (valueFormat === 'money' ? formatCompactMoney(Number(v)) : formatChartCount(Number(v))),
    [valueFormat],
  );

  if (!allowedModes.length || !activeMode) return null;

  return (
    <div className="rounded-xl border border-app-border bg-app-surface p-4 shadow-sm sm:p-5">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-sm font-semibold text-app-text">{activeMode.title}</h2>
          <p className="mt-0.5 text-xs text-app-text-subtle">{subtitle}</p>
        </div>
        <div
          className={`grid w-full items-center gap-0.5 rounded-lg border border-app-border bg-app-surface-muted p-0.5 sm:inline-flex sm:w-auto ${
            allowedModes.length === 1 ? 'grid-cols-1' : allowedModes.length === 2 ? 'grid-cols-2' : 'grid-cols-3'
          }`}
        >
          {allowedModes.map((option) => {
            const active = option.id === mode;
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => setMode(option.id)}
                className={`rounded-md px-2 py-2 text-center text-[11px] font-semibold transition-colors sm:px-2.5 sm:py-1 sm:text-xs ${
                  active ? 'bg-app-surface text-app-text shadow-sm' : 'text-app-text-subtle hover:text-app-text-muted'
                }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      <div ref={chartBoxRef} className="h-[260px] overflow-hidden sm:h-[300px]">
        {!hasData ? (
          <div className="flex h-full items-center justify-center text-sm text-app-text-subtle">
            Noch keine Daten in diesem Zeitraum.
          </div>
        ) : chartSize.width > 0 && chartSize.height > 0 ? (
            <BarChart
              width={chartSize.width}
              height={chartSize.height}
              data={points}
              margin={CHART_MARGIN}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-app-border)" />
              <XAxis dataKey="label" tick={X_AXIS_TICK} axisLine={false} tickLine={false} />
              <YAxis
                tick={Y_AXIS_TICK}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
                width={42}
                tickFormatter={formatYTick}
              />
              <Tooltip
                cursor={CHART_CURSOR}
                content={valueFormat === 'money' ? MoneyTooltip : CountTooltip}
              />
              {series.map((s, index) => (
                <Bar
                  key={s.dataKey}
                  dataKey={s.dataKey}
                  name={s.name}
                  stackId="stack"
                  fill={s.color}
                  radius={index === series.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                  maxBarSize={38}
                  isAnimationActive={false}
                />
              ))}
            </BarChart>
        ) : null}
      </div>
      {hasData ? (
        <ul className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
          {series.map((s) => (
            <li key={s.dataKey} className="flex items-center gap-1.5 text-[11px] text-app-text-muted">
              <span className="inline-block h-2 w-2 rounded-sm" style={{ background: s.color }} />
              {s.name}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
